// ============================================================
// useCharacterAdmission - the one door characters come in through.
//
// Three ways in: a bulk load onto the roster, the spawn dialog's count,
// and staging into a waiting room. Each of them had learned every rule
// separately - instance numbering (v8.35.0), importing into the session
// library (v8.26.0 for staging, v8.37.0 for loading) and recording a
// recent load (v8.37.0) - and the same three-line change was written
// into three functions twice in one night, each time fixing one door
// and waiting for a report about the others.
//
// The RULE is pure and lives in `engine/roster/admission`. This is the
// part that cannot be: taking the census from live state, and the three
// side effects that follow every admission whichever door it came
// through. A fourth rule now costs one line here instead of three
// scattered ones, and cannot be applied to two doors out of three
// (v8.47.0).
// ============================================================
import { useCallback, useMemo, useRef } from 'react';
import { sessionInstances } from '../engine/roster/naming';
import { admitCharacters } from '../engine/roster/admission';

/**
 * @param {object} args
 * @param {Array} args.roster        every combatant on the table
 * @param {Array} args.rooms         every waiting room, entries and all
 * @param {object} args.library      the active character library
 *
 * `censusRef` is EVERYBODY WHO EXISTS, for naming the next arrival: a ref
 * beside the memo so the doors can read it without any of them taking the
 * whole roster as a dependency and re-making itself on every wound tick -
 * the same idiom as `addNarrativeRef`.
 *
 * `admit` names a batch and says who is distinct in it. Take it BEFORE
 * mutating anything: staging adds to a room, and a census taken afterwards
 * would count the arrivals as though they were already here.
 *
 * `record` is what follows an admission however it happened: the
 * session's character list learns about them, and they reach "Recently
 * Loaded". Both take the DISTINCT set - three of somebody are three
 * combatants and one library entry; the list is of characters, not of
 * bodies. `admitAndRecord` is both halves, for a door with nothing special
 * to do between them.
 */
export default function useCharacterAdmission({ roster, rooms, library }) {
  const censusRef = useRef([]);
  censusRef.current = useMemo(() => sessionInstances(roster, rooms), [roster, rooms]);
  const admit = useCallback((characters) => admitCharacters(censusRef.current, characters), []);
  const record = useCallback(
    (distinct) => {
      if (!distinct?.length) return;
      library.importCharacters(JSON.stringify(distinct));
      distinct.forEach((c) => library.addRecentLoad(c.id));
    },
    [library],
  );
  const admitAndRecord = useCallback(
    (characters) => {
      const admission = admit(characters);
      record(admission.distinct);
      return admission;
    },
    [admit, record],
  );
  return { censusRef, admit, record, admitAndRecord };
}
